import { Coins, TrendingUp } from 'lucide-react'
import { formatCredits, type CreditsSummary, type SubscriptionStatus } from '@/api/billing'

/**
 * The headline: how many credits are left, and when the plan renews.
 *
 * Credits only — no currency. Money appears on the top-up confirm modal and on
 * invoices, nowhere else.
 */
export default function BalanceCard({
  summary, subscription, onTopUp,
}: {
  summary: CreditsSummary | null
  subscription: SubscriptionStatus | null
  onTopUp: () => void
}) {
  const balance = summary?.balance ?? null
  const low =
    balance !== null &&
    summary?.low_balance_threshold != null &&
    balance <= summary.low_balance_threshold

  const renewal = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric',
      })
    : null

  return (
    <div className="card" style={{ padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
        <div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            fontSize: '0.875rem', color: 'var(--color-text-secondary)',
          }}>
            <Coins size={16} style={{ color: 'var(--color-accent)' }} />
            Credit balance
          </div>
          <div style={{
            fontSize: '2.3125rem', fontWeight: 700, lineHeight: 1.2, marginTop: '0.25rem',
            color: low ? 'var(--color-warning, #f0a020)' : 'var(--color-text-primary)',
          }}>
            {formatCredits(balance)}
          </div>
          {/* The wallet that pays is the organization's, so say so — otherwise a
              member wonders why topping up personally changed nothing. */}
          {summary?.scope === 'tenant' && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
              AI usage is billed to your organization's shared wallet.
            </div>
          )}
          {low && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-warning, #f0a020)', marginTop: '0.25rem' }}>
              Running low — top up to avoid interruption.
            </div>
          )}
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Plan</div>
          <div style={{ fontWeight: 600 }}>{summary?.plan_label || '—'}</div>
          {renewal && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
              {subscription?.cancel_at_period_end ? 'Ends' : 'Renews'} {renewal}
            </div>
          )}
          <button className="btn btn-primary" style={{ marginTop: '0.75rem', gap: '0.375rem' }} onClick={onTopUp}>
            <TrendingUp size={16} /> Top up
          </button>
        </div>
      </div>
    </div>
  )
}
